/**
 * Turn a document-viewer load failure into a sentence a person can act on.
 *
 * WHY THIS EXISTS. Both viewers used to assign the raw exception message straight to the error
 * panel (`err?.message || 'Failed to load document'`). Two things were wrong with that, and the
 * second one cost a full investigation:
 *
 *   - it printed English into a Spanish UI, since the fallback was a hardcoded literal; and
 *   - `fetch` rejects with `TypeError: Failed to fetch` for EVERY transport-level failure — a
 *     dropped connection, a CORS refusal, an offline client — so the panel showed three words that
 *     named neither the cause nor the layer. MAR-455 was filed against the storage read path on the
 *     strength of that string, when the actual cause was a dev server restarting mid-request.
 *
 * The two viewers fail differently and both are handled here: `DocxViewer` fetches bytes itself and
 * attaches `status` to the error, while `PdfViewer` hands the URL to pdf.js, which raises its own
 * `ResponseException` / `InvalidPDFException` / `PasswordException` (v6 — `ResponseException`
 * carries both `status` and `missing`).
 *
 * The caller still logs the full error; this is only what the user reads.
 */
export function viewerErrorMessage(err: any, t: (key: string) => string): string {
  const name: string = err?.name ?? ''

  // pdf.js semantic failures — the file arrived, but it cannot be shown.
  if (name === 'PasswordException') return t('viewer.errorPassword')
  if (name === 'InvalidPDFException') return t('viewer.errorCorrupt')

  // `missing` is how pdf.js reports a 404 that the server dressed up as something else.
  if (err?.missing === true) return t('viewer.errorNotFound')

  const status: number | undefined = typeof err?.status === 'number' ? err.status : undefined
  if (status !== undefined) {
    if (status === 404 || status === 410) return t('viewer.errorNotFound')
    if (status === 401 || status === 403) return t('viewer.errorForbidden')
    if (status >= 500) return t('viewer.errorServer')
    return t('viewer.errorGeneric')
  }

  // No status at all means the request never completed: the connection was refused, reset or
  // blocked. `fetch` signals this as a TypeError; pdf.js surfaces it as UnknownErrorException.
  if (err instanceof TypeError || name === 'TypeError' || name === 'UnknownErrorException') {
    return t('viewer.errorNetwork')
  }

  return t('viewer.errorGeneric')
}
